import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowUpRight, ArrowDownRight, Calendar, DollarSign, Clock, AlertCircle, TrendingUp, AlertTriangle } from "lucide-react";
import { format, parseISO, isBefore, addDays, startOfMonth, endOfMonth, addMonths, startOfQuarter, addQuarters } from "date-fns";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Line, LineChart, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Bar, BarChart } from "recharts";
import { EntitySwitcher } from "@/components/EntitySwitcher";
import { useEntity } from "@/hooks/useEntity";

// Asset class classification helper
const classifyAltVehicleAssetClass = (type: string | null, name: string | null): string => {
  const t = (type || '').toLowerCase();
  const n = (name || '').toLowerCase();

  if (t.includes('pe') || t.includes('buyout') || n.includes('private equity')) return 'Private Equity';
  if (t.includes('vc') || t.includes('venture') || n.includes('venture')) return 'Venture Capital';
  if (t.includes('real estate') || t.includes('reit') || n.includes('real estate') || n.includes('property')) return 'Real Estate';
  if (t.includes('infra') || n.includes('infrastructure')) return 'Infrastructure';
  if (t.includes('credit') || t.includes('debt') || t.includes('lending') || n.includes('credit')) return 'Private Credit';
  if (t.includes('hedge') || n.includes('hedge')) return 'Hedge Fund';
  return 'Other Alternatives';
};

// Mock data for cash forecast
const generateCashForecast = () => {
  const quarters = [];
  let cashBalance = 12000000; // Starting cash
  const today = new Date();
  
  for (let i = 0; i < 8; i++) {
    const quarter = addQuarters(startOfQuarter(today), i);
    const quarterName = `Q${Math.floor(quarter.getMonth() / 3) + 1} ${quarter.getFullYear()}`;
    
    // Mock quarterly data
    const dividends = Math.random() * 1500000 + 500000;
    const capitalCalls = Math.random() * 3000000 + 1000000;
    const otherInflows = Math.random() * 500000;
    const otherOutflows = Math.random() * 800000;
    
    const netFlow = dividends + otherInflows - capitalCalls - otherOutflows;
    cashBalance += netFlow;
    
    quarters.push({
      quarter: quarterName,
      openingBalance: cashBalance - netFlow,
      dividends,
      capitalCalls,
      otherInflows,
      otherOutflows,
      netFlow,
      closingBalance: cashBalance,
      isLowCash: cashBalance < 5000000,
    });
  }
  
  return quarters;
};

export default function Liquidity() {
  const { orgId, familyId } = useTenant();
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [entityId, setEntityId] = useState<string | null>(null);
  const cashForecast = generateCashForecast();
  const { entities } = useEntity();
  
  const selectedEntity = entities.find(e => e.id === entityId);
  
  // Fetch capital events
  const { data: capitalEvents = [], isLoading } = useQuery({
    queryKey: ['capital-events', orgId, familyId, statusFilter, typeFilter, entityId],
    queryFn: async () => {
      let query = supabase
        .from('capital_events')
        .select('*')
        .eq('org_id', orgId)
        .order('due_at', { ascending: true });
      
      if (familyId) {
        query = query.eq('family_id', familyId);
      }
      
      if (entityId) {
        query = query.eq('entity_id', entityId);
      }
      
      if (statusFilter !== "all") {
        query = query.eq('status', statusFilter);
      }
      
      if (typeFilter !== "all") {
        query = query.eq('kind', typeFilter);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
    enabled: !!orgId,
  });

  // Fetch commitments from alt_vehicles with metrics
  const { data: commitmentsData = [] } = useQuery({
    queryKey: ['commitments', orgId, familyId, entityId],
    queryFn: async () => {
      // Fetch alt vehicles
      let query = supabase
        .from('alt_vehicles')
        .select('*')
        .eq('org_id', orgId);

      if (familyId) {
        query = query.eq('family_id', familyId);
      }

      if (entityId) {
        query = query.eq('entity_id', entityId);
      }

      const { data: vehicles, error: vehiclesError } = await query;
      if (vehiclesError) throw vehiclesError;

      if (!vehicles || vehicles.length === 0) return [];

      // Fetch latest metrics for these vehicles
      const vehicleIds = vehicles.map(v => v.id);
      const { data: metrics, error: metricsError } = await supabase
        .from('alt_period_metrics')
        .select('*')
        .in('vehicle_id', vehicleIds)
        .order('period_end', { ascending: false });

      if (metricsError) throw metricsError;

      // Get latest metrics per vehicle
      const latestMetrics: Record<string, { vehicle_id: string; called_base?: number; unfunded_base?: number; period_end: string }> = {};
      metrics?.forEach(m => {
        if (!latestMetrics[m.vehicle_id]) {
          latestMetrics[m.vehicle_id] = m;
        }
      });

      // Aggregate by asset class
      const byAssetClass: Record<string, {
        assetClass: string;
        totalCommitment: number;
        funded: number;
        unfunded: number;
        expectedCallsNext12M: number;
      }> = {};

      vehicles.forEach(v => {
        const assetClass = classifyAltVehicleAssetClass(v.type, v.fund_name);
        const metric = latestMetrics[v.id];

        if (!byAssetClass[assetClass]) {
          byAssetClass[assetClass] = {
            assetClass,
            totalCommitment: 0,
            funded: 0,
            unfunded: 0,
            expectedCallsNext12M: 0,
          };
        }

        const commitment = Number(v.commitment_base) || 0;
        const called = Number(metric?.called_base) || 0;
        const unfunded = Number(metric?.unfunded_base) || (commitment - called);

        byAssetClass[assetClass].totalCommitment += commitment;
        byAssetClass[assetClass].funded += called;
        byAssetClass[assetClass].unfunded += unfunded;
        // Estimate 12M calls as ~50% of unfunded for active funds
        byAssetClass[assetClass].expectedCallsNext12M += unfunded * 0.5;
      });

      return Object.values(byAssetClass).sort((a, b) => b.totalCommitment - a.totalCommitment);
    },
    enabled: !!orgId,
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getDaysUntil = (dueDate: string) => {
    const due = parseISO(dueDate);
    const today = new Date();
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getUrgencyBadge = (dueDate: string) => {
    const days = getDaysUntil(dueDate);
    if (days < 0) return <Badge variant="destructive">Overdue</Badge>;
    if (days <= 7) return <Badge variant="destructive">{days}d</Badge>;
    if (days <= 30) return <Badge variant="default">{days}d</Badge>;
    return <Badge variant="secondary">{days}d</Badge>;
  };

  // Calculate summary stats
  const totalCalls = capitalEvents
    .filter(e => e.kind === 'call' && e.status === 'expected')
    .reduce((sum, e) => sum + (e.amount || 0), 0);
    
  const totalDistributions = capitalEvents
    .filter(e => e.kind === 'distribution' && e.status === 'expected')
    .reduce((sum, e) => sum + (e.amount || 0), 0);
    
  const netLiquidity = totalDistributions - totalCalls;
  
  const overdueCount = capitalEvents.filter(e => 
    e.due_at && isBefore(parseISO(e.due_at), new Date()) && e.status === 'expected'
  ).length;

  // Group capital calls by month for line chart
  const callsByMonth = capitalEvents
    .filter(e => e.kind === 'call' && e.status === 'expected' && e.due_at)
    .reduce((acc, event) => {
      const date = parseISO(event.due_at!);
      const monthKey = format(date, 'MMM yyyy');
      
      if (!acc[monthKey]) {
        acc[monthKey] = {
          month: monthKey,
          amount: 0,
          date: startOfMonth(date)
        };
      }
      
      acc[monthKey].amount += event.amount || 0;
      
      return acc;
    }, {} as Record<string, { month: string; amount: number; date: Date }>);

  const chartData = Object.values(callsByMonth)
    .sort((a, b) => a.date.getTime() - b.date.getTime())
    .map(({ month, amount }) => ({ month, amount }));

  // Get events for selected month
  const selectedMonthEvents = selectedMonth
    ? capitalEvents.filter(e => {
        if (e.kind !== 'call' || e.status !== 'expected' || !e.due_at) return false;
        const monthKey = format(parseISO(e.due_at), 'MMM yyyy');
        return monthKey === selectedMonth;
      })
    : [];

  const totalCommitments = commitmentsData.reduce((sum, c) => sum + c.totalCommitment, 0);
  const totalFunded = commitmentsData.reduce((sum, c) => sum + c.funded, 0);
  const totalUnfunded = commitmentsData.reduce((sum, c) => sum + c.unfunded, 0);
  const totalExpectedCalls = commitmentsData.reduce((sum, c) => sum + c.expectedCallsNext12M, 0);

  const CapitalEventsView = () => (
    <div className="space-y-4">

      {/* Summary KPIs */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Expected Calls</CardTitle>
            <ArrowUpRight className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{formatCurrency(totalCalls)}</div>
            <p className="text-xs text-muted-foreground">Capital required</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Expected Distributions</CardTitle>
            <ArrowDownRight className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{formatCurrency(totalDistributions)}</div>
            <p className="text-xs text-muted-foreground">Incoming liquidity</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Net Position</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${netLiquidity >= 0 ? 'text-green-600' : 'text-destructive'}`}>
              {formatCurrency(netLiquidity)}
            </div>
            <p className="text-xs text-muted-foreground">Expected net flow</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Overdue</CardTitle>
            <AlertCircle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{overdueCount}</div>
            <p className="text-xs text-muted-foreground">Requires attention</p>
          </CardContent>
        </Card>
      </div>

      {/* Capital Calls Timeline Chart */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Capital Calls Timeline
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Click on any month to see details
            </p>
          </CardHeader>
          <CardContent>
            {chartData.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <div className="text-center">
                  <Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No upcoming capital calls scheduled</p>
                </div>
              </div>
            ) : (
              <ChartContainer
                config={{
                  amount: {
                    label: "Capital Calls",
                    color: "hsl(var(--destructive))",
                  },
                }}
                className="h-[300px]"
              >
                <LineChart data={chartData} onClick={(e) => {
                  if (e && e.activeLabel) {
                    setSelectedMonth(e.activeLabel as string);
                  }
                }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis
                    dataKey="month"
                    className="text-xs"
                    tick={{ fill: "hsl(var(--muted-foreground))" }}
                  />
                  <YAxis
                    className="text-xs"
                    tick={{ fill: "hsl(var(--muted-foreground))" }}
                    tickFormatter={(value) => `$${(value / 1000000).toFixed(1)}M`}
                  />
                  <ChartTooltip
                    content={
                      <ChartTooltipContent
                        formatter={(value) =>
                          new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                          }).format(value as number)
                        }
                      />
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey="amount"
                    stroke="hsl(var(--destructive))"
                    strokeWidth={2}
                    dot={{ fill: "hsl(var(--destructive))", r: 4 }}
                  />
                </LineChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Capital Calls this Quarter</CardTitle>
            <p className="text-sm text-muted-foreground">
              {selectedMonth || 'Select a month'}
            </p>
          </CardHeader>
          <CardContent>
            {!selectedMonth ? (
              <div className="text-center text-muted-foreground py-8">
                <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Click on the chart to view details</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <div className="text-2xl font-bold text-destructive">
                    {formatCurrency(selectedMonthEvents.reduce((sum, e) => sum + (e.amount || 0), 0))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {selectedMonthEvents.length} call{selectedMonthEvents.length !== 1 ? 's' : ''}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <p className="text-sm font-medium">Funds:</p>
                  {selectedMonthEvents.slice(0, 5).map((event) => (
                    <div key={event.id} className="flex justify-between items-start text-sm">
                      <span className="text-muted-foreground flex-1 mr-2">{event.fund_name || 'Unknown'}</span>
                      <span className="font-mono font-medium">{formatCurrency(event.amount || 0)}</span>
                    </div>
                  ))}
                  {selectedMonthEvents.length > 5 && (
                    <p className="text-xs text-muted-foreground">
                      +{selectedMonthEvents.length - 5} more
                    </p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Filters and Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Capital Events</CardTitle>
            <div className="flex gap-2">
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="call">Calls</SelectItem>
                  <SelectItem value="distribution">Distributions</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="expected">Expected</SelectItem>
                  <SelectItem value="received">Received</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              Loading capital events...
            </div>
          ) : capitalEvents.length === 0 ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              No capital events found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Fund</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Currency</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Urgency</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {capitalEvents.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell>
                      <Badge variant={event.kind === "call" ? "destructive" : "default"}>
                        {event.kind === "call" ? (
                          <>
                            <ArrowUpRight className="h-3 w-3 mr-1" />
                            Call
                          </>
                        ) : (
                          <>
                            <ArrowDownRight className="h-3 w-3 mr-1" />
                            Distribution
                          </>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">{event.fund_name || 'N/A'}</TableCell>
                    <TableCell className="font-mono">{formatCurrency(event.amount || 0)}</TableCell>
                    <TableCell>{event.ccy}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        {event.due_at ? format(parseISO(event.due_at), 'MMM dd, yyyy') : 'N/A'}
                      </div>
                    </TableCell>
                    <TableCell>
                      {event.due_at && getUrgencyBadge(event.due_at)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {event.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const CommitmentsView = () => (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Commitments</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalCommitments)}</div>
            <p className="text-xs text-muted-foreground">Across all asset classes</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Funded</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success">{formatCurrency(totalFunded)}</div>
            <p className="text-xs text-muted-foreground">
              {((totalFunded / totalCommitments) * 100).toFixed(1)}% of commitments
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Unfunded</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-warning">{formatCurrency(totalUnfunded)}</div>
            <p className="text-xs text-muted-foreground">
              {((totalUnfunded / totalCommitments) * 100).toFixed(1)}% remaining
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Expected Calls (12M)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{formatCurrency(totalExpectedCalls)}</div>
            <p className="text-xs text-muted-foreground">Next 12 months</p>
          </CardContent>
        </Card>
      </div>

      {/* Commitments by Asset Class Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Commitments by Asset Class</CardTitle>
          <CardDescription>Total commitments, funded, and unfunded amounts</CardDescription>
        </CardHeader>
        <CardContent>
          <ChartContainer
            config={{
              totalCommitment: { label: "Total", color: "hsl(var(--chart-1))" },
              funded: { label: "Funded", color: "hsl(var(--chart-2))" },
              unfunded: { label: "Unfunded", color: "hsl(var(--chart-3))" },
            }}
            className="h-[350px]"
          >
            <BarChart data={commitmentsData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis 
                dataKey="assetClass" 
                angle={-45} 
                textAnchor="end" 
                height={100}
                tick={{ fill: "hsl(var(--muted-foreground))" }}
              />
              <YAxis 
                tick={{ fill: "hsl(var(--muted-foreground))" }}
                tickFormatter={(value) => `$${(value / 1000000).toFixed(0)}M`}
              />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    formatter={(value) => formatCurrency(value as number)}
                  />
                }
              />
              <Bar dataKey="funded" fill="hsl(var(--chart-2))" radius={[4, 4, 0, 0]} />
              <Bar dataKey="unfunded" fill="hsl(var(--chart-3))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ChartContainer>
        </CardContent>
      </Card>

      {/* Detailed Table */}
      <Card>
        <CardHeader>
          <CardTitle>Commitment Details</CardTitle>
          <CardDescription>Breakdown by asset class with expected capital calls</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Asset Class</TableHead>
                <TableHead className="text-right">Total Commitment</TableHead>
                <TableHead className="text-right">Funded</TableHead>
                <TableHead className="text-right">Unfunded</TableHead>
                <TableHead className="text-right">% Funded</TableHead>
                <TableHead className="text-right">Expected Calls (12M)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {commitmentsData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    No commitment data found. Upload fund statements to populate.
                  </TableCell>
                </TableRow>
              ) : commitmentsData.map((commitment, idx) => (
                <TableRow key={idx}>
                  <TableCell className="font-medium">{commitment.assetClass}</TableCell>
                  <TableCell className="text-right font-mono">{formatCurrency(commitment.totalCommitment)}</TableCell>
                  <TableCell className="text-right font-mono text-success">{formatCurrency(commitment.funded)}</TableCell>
                  <TableCell className="text-right font-mono text-warning">{formatCurrency(commitment.unfunded)}</TableCell>
                  <TableCell className="text-right">
                    <Badge variant="outline">
                      {commitment.totalCommitment > 0 ? ((commitment.funded / commitment.totalCommitment) * 100).toFixed(1) : 0}%
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono text-destructive">
                    {formatCurrency(commitment.expectedCallsNext12M)}
                  </TableCell>
                </TableRow>
              ))}
              {commitmentsData.length > 0 && (
                <TableRow className="border-t-2 bg-primary/5 font-bold">
                  <TableCell>Total</TableCell>
                  <TableCell className="text-right">{formatCurrency(totalCommitments)}</TableCell>
                  <TableCell className="text-right text-success">{formatCurrency(totalFunded)}</TableCell>
                  <TableCell className="text-right text-warning">{formatCurrency(totalUnfunded)}</TableCell>
                  <TableCell className="text-right">
                    <Badge variant="default">
                      {totalCommitments > 0 ? ((totalFunded / totalCommitments) * 100).toFixed(1) : 0}%
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right text-destructive">{formatCurrency(totalExpectedCalls)}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );

  const CashForecastView = () => {
    const lowCashQuarters = cashForecast.filter(q => q.isLowCash).length;
    const avgQuarterlyCalls = cashForecast.reduce((sum, q) => sum + q.capitalCalls, 0) / cashForecast.length;
    const avgQuarterlyDividends = cashForecast.reduce((sum, q) => sum + q.dividends, 0) / cashForecast.length;

    return (
      <div className="space-y-4">
        {/* Summary Cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Current Cash Balance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(cashForecast[0].openingBalance)}</div>
              <p className="text-xs text-muted-foreground">As of today</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Avg Quarterly Dividends</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-success">{formatCurrency(avgQuarterlyDividends)}</div>
              <p className="text-xs text-muted-foreground">Expected income</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Avg Quarterly Calls</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-destructive">{formatCurrency(avgQuarterlyCalls)}</div>
              <p className="text-xs text-muted-foreground">Expected outflow</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-warning" />
                Low Cash Alerts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-warning">{lowCashQuarters}</div>
              <p className="text-xs text-muted-foreground">Quarters below $5M</p>
            </CardContent>
          </Card>
        </div>

        {/* Cash Balance Timeline */}
        <Card>
          <CardHeader>
            <CardTitle>Cash Balance Forecast</CardTitle>
            <CardDescription>Projected quarterly cash balance for next 24 months</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer
              config={{
                closingBalance: { label: "Cash Balance", color: "hsl(var(--chart-1))" },
              }}
              className="h-[300px]"
            >
              <LineChart data={cashForecast}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="quarter"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  tick={{ fill: "hsl(var(--muted-foreground))" }}
                />
                <YAxis
                  tick={{ fill: "hsl(var(--muted-foreground))" }}
                  tickFormatter={(value) => `$${(value / 1000000).toFixed(1)}M`}
                />
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      formatter={(value) => formatCurrency(value as number)}
                    />
                  }
                />
                <Line
                  type="monotone"
                  dataKey="closingBalance"
                  stroke="hsl(var(--chart-1))"
                  strokeWidth={2}
                  dot={(props: { cx?: number; cy?: number; index?: number }) => {
                    const isLow = props.index !== undefined && cashForecast[props.index]?.isLowCash;
                    return (
                      <circle
                        cx={props.cx}
                        cy={props.cy}
                        r={4}
                        fill={isLow ? "hsl(var(--warning))" : "hsl(var(--chart-1))"}
                        stroke={isLow ? "hsl(var(--warning))" : "hsl(var(--chart-1))"}
                      />
                    );
                  }}
                />
              </LineChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Detailed Forecast Table */}
        <Card>
          <CardHeader>
            <CardTitle>Quarterly Cash Flow Forecast</CardTitle>
            <CardDescription>Detailed breakdown of expected cash movements</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Quarter</TableHead>
                  <TableHead className="text-right">Opening Balance</TableHead>
                  <TableHead className="text-right">Dividends</TableHead>
                  <TableHead className="text-right">Capital Calls</TableHead>
                  <TableHead className="text-right">Other In/Out</TableHead>
                  <TableHead className="text-right">Net Flow</TableHead>
                  <TableHead className="text-right">Closing Balance</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cashForecast.map((quarter, idx) => (
                  <TableRow key={idx} className={quarter.isLowCash ? "bg-warning/10" : ""}>
                    <TableCell className="font-medium">{quarter.quarter}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(quarter.openingBalance)}</TableCell>
                    <TableCell className="text-right font-mono text-success">
                      +{formatCurrency(quarter.dividends)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-destructive">
                      -{formatCurrency(quarter.capitalCalls)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-muted-foreground">
                      {formatCurrency(quarter.otherInflows - quarter.otherOutflows)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      <span className={quarter.netFlow >= 0 ? "text-success" : "text-destructive"}>
                        {quarter.netFlow >= 0 ? "+" : ""}{formatCurrency(quarter.netFlow)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-semibold">{formatCurrency(quarter.closingBalance)}</TableCell>
                    <TableCell>
                      {quarter.isLowCash && (
                        <Badge variant="outline" className="gap-1 text-warning border-warning">
                          <AlertTriangle className="h-3 w-3" />
                          Low Cash
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="flex-1 space-y-4 px-6 py-6">
      
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight">Liquidity & Capital Events</h1>
            {selectedEntity && (
              <Badge variant="secondary">{selectedEntity.name}</Badge>
            )}
          </div>
          <p className="text-muted-foreground">
            Track capital calls, distributions, commitments, and cash forecasts
          </p>
        </div>
        <EntitySwitcher value={entityId} onValueChange={setEntityId} />
      </div>

      <Tabs defaultValue="events" className="space-y-4">
        <TabsList>
          <TabsTrigger value="events">Capital Events</TabsTrigger>
          <TabsTrigger value="commitments">Commitments</TabsTrigger>
          <TabsTrigger value="forecast">Cash Forecast</TabsTrigger>
        </TabsList>
        
        <TabsContent value="events" className="space-y-4">
          <CapitalEventsView />
        </TabsContent>
        
        <TabsContent value="commitments" className="space-y-4">
          <CommitmentsView />
        </TabsContent>
        
        <TabsContent value="forecast" className="space-y-4">
          <CashForecastView />
        </TabsContent>
      </Tabs>
    </div>
  );
}
